import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { reportesApi, type RankingOperador } from '../api/reportes'
import { Table, type Column } from '../components/ui/Table'
import { fixed } from '../utils/format'

const MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' }

function SkeletonKPI() {
  return (
    <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-2">
      <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      <div className="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
    </div>
  )
}

export default function Reportes() {
  const [mes, setMes] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })

  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data: rankingData, isLoading: rankingLoading } = useQuery({
    queryKey: ['ranking-operadores', mes],
    queryFn: () => reportesApi.getRankingOperadores(mes),
  })

  const { data: resumenData, isLoading: resumenLoading } = useQuery({
    queryKey: ['resumen-mes', mes],
    queryFn: () => reportesApi.getResumen(mes),
  })

  const rankingItems = rankingData?.items ?? []
  const resumen = resumenData?.resumen

  const RANKING_COLUMNS: Column<RankingOperador>[] = [
    {
      key: 'posicion',
      header: '#',
      render: (row) => (
        <span className="font-bold">
          {MEDALS[row.posicion] ?? row.posicion}
        </span>
      ),
    },
    {
      key: 'nombre',
      header: 'Operador',
      render: (row) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">{row.nombre}</span>
      ),
    },
    {
      key: 'litros',
      header: 'Litros',
      sortable: true,
      render: (row) => `${Number(row.litros).toFixed(0)} L`,
      className: 'tabular-nums',
    },
    {
      key: 'cargas',
      header: 'Cargas',
      render: (row) => row.cargas,
      className: 'tabular-nums',
    },
    {
      key: 'outliers',
      header: 'Outliers',
      render: (row) => (
        <span className={row.outliers > 0 ? 'text-orange-600 dark:text-orange-400 font-medium' : ''}>
          {row.outliers}
        </span>
      ),
      className: 'tabular-nums',
    },
    {
      key: 'porcentajeOutlier',
      header: '% Outlier',
      render: (row) => (
        <span className={row.porcentajeOutlier > 15 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
          {fixed(row.porcentajeOutlier, 1)}%
        </span>
      ),
      className: 'tabular-nums',
    },
    {
      key: 'montoBob',
      header: 'Monto BOB',
      render: (row) => `Bs. ${Number(row.montoBob).toFixed(2)}`,
      className: 'tabular-nums',
    },
  ]

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Reportes</h1>

      {/* Reporte mensual + descarga */}
      <Card>
        <CardHeader title="Reporte mensual" subtitle="Genera el informe PPT/PDF del mes seleccionado" />
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="mes-picker" className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Mes
            </label>
            <input
              id="mes-picker"
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
              className="border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <a href={`${apiUrl}/api/reportes/mensual.pptx?mes=${mes}`} download>
            <Button variant="primary" size="md">Descargar PPT</Button>
          </a>
          <a
            href={`${apiUrl}/api/reportes/mensual.pdf?mes=${mes}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="secondary" size="md">Ver PDF / Imprimir</Button>
          </a>
        </div>
      </Card>

      {/* KPIs resumen del mes */}
      <div>
        <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">
          Resumen del mes
        </h2>
        {resumenLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)}
          </div>
        ) : resumen ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Litros</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                {Number(resumen.totalLitros).toFixed(0)} L
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Monto BOB</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                Bs. {Number(resumen.montoBob).toFixed(0)}
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Total Cargas</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                {resumen.totalCargas}
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Outliers %</p>
              <p className={`text-2xl font-bold tabular-nums ${
                resumen.outlierPorcentaje > 10
                  ? 'text-red-600 dark:text-red-400'
                  : resumen.outlierPorcentaje > 5
                  ? 'text-orange-600 dark:text-orange-400'
                  : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {fixed(resumen.outlierPorcentaje, 1)}%
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-6 text-center text-slate-400 text-sm">
            No hay datos de resumen para este mes
          </div>
        )}
      </div>

      {/* Ranking operadores */}
      <Card>
        <CardHeader
          title="Ranking de operadores"
          subtitle={`Ordenado por litros cargados · ${mes}`}
        />
        <Table
          columns={RANKING_COLUMNS}
          data={rankingItems}
          keyExtractor={(r) => r.operadorId}
          loading={rankingLoading}
          emptyMessage="Sin datos de operadores para este mes"
        />
      </Card>
    </div>
  )
}
